import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from '@mui/material';
import axios from 'axios';
import { getToken } from '../../authetication/loginState';
import Spinner from "../../components/SectionSpinner/spinner";
import Resume from "../../images/resume.png";
import ReferralRewardsModal from "../../components/ReferralRewardsModal";

const Profile = () => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const navigate = useNavigate();
    const userEmail = localStorage.getItem("userEmail");

    const [isLoading, setIsLoading] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [resumeUrl, setResumeUrl] = useState(null);
    const [resumeFileName, setResumeFileName] = useState("");
    const [hasResume, setHasResume] = useState(false);
    const [jobAlertsEnabled, setJobAlertsEnabled] = useState(true);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [openReferralModal, setOpenReferralModal] = useState(false);

    useEffect(() => {
        if (!getToken()) {
            localStorage.clear();
            navigate("/login");
            return;
        }

        fetchUserResume();
        checkSubscription();
        getJobAlertsStatus();
    }, [navigate]);

    const fetchUserResume = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/Chat/getUserResume`,
                {
                    headers: {
                        Authorization: `Bearer ${getToken()}`
                    }
                }
            );

            if (response.data.hasResume && response.data.resumeUrl) {
                setHasResume(true);
                setResumeUrl(response.data.resumeUrl);

                // Extract clean filename
                const urlParts = response.data.resumeUrl.split('/');
                const fullFileName = urlParts[urlParts.length - 1];
                const match = fullFileName.match(/^resume-(.+)-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}(\..+)$/i);
                
                if (match) {
                    setResumeFileName(`${match[1]}${match[2]}`);
                } else {
                    setResumeFileName(fullFileName);
                }
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching user resume:', error);
            setIsLoading(false);
        }
    };

    const checkSubscription = async () => {
        try {
            const subscriptionCheck = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/Chat/checkSubscription`,
                { email: userEmail },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: `Bearer ${getToken()}`,
                    },
                }
            );

            setIsSubscribed(subscriptionCheck.data.isSubscribed);
        } catch (error) {
            console.error('Error checking subscription:', error);
        }
    };

    const handleDeleteResume = async () => {
        if (window.confirm('Are you sure you want to delete your saved resume?')) {
            try {
                setIsLoading(true);
                await axios.delete(
                    `${process.env.REACT_APP_API_ENDPOINT}/Chat/deleteUserResume`,
                    {
                        headers: {
                            Authorization: `Bearer ${getToken()}`,
                        },
                    }
                );
                
                // Clear local state
                setHasResume(false);
                setResumeUrl(null);
                setResumeFileName("");
                localStorage.removeItem('resume');
                localStorage.removeItem('resumeFileName');
                
                alert('Resume deleted successfully!');
                setIsLoading(false);
            } catch (error) {
                console.error('Error deleting resume:', error);
                alert('Failed to delete resume. Please try again.');
                setIsLoading(false);
            }
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setIsLoading(true);

            // Read file and convert to base64
            const reader = new FileReader();
            reader.onload = async function(event) {
                localStorage.setItem("resume", event.target.result);
                localStorage.setItem("resumeFileName", file.name);
                
                // Upload will happen automatically during next interview
                setResumeFileName(file.name);
                setHasResume(true);
                alert('Resume uploaded successfully! It will be saved during your next interview.');
                setIsLoading(false);
            };
            reader.readAsDataURL(file);
        } catch (error) {
            console.error('Error uploading resume:', error);
            alert('Failed to upload resume. Please try again.');
            setIsLoading(false);
        }
    };

    // Function to get document viewer URL
    const getDocumentViewerUrl = (fileUrl) => {
        return `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    };

    const getJobAlertsStatus = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_ENDPOINT}/Chat/getJobAlertsStatus`,
                {
                    headers: {
                        Authorization: `Bearer ${getToken()}`,
                    },
                }
            );
            setJobAlertsEnabled(response.data.jobAlertsEnabled);
        } catch (error) {
            console.error('Error getting job alerts status:', error);
        }
    };

    const toggleJobAlerts = async () => {
        try {
            const newStatus = !jobAlertsEnabled;
            const response = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/Chat/toggleJobAlerts`,
                { enabled: newStatus },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${getToken()}`,
                    },
                }
            );

            if (response.data.success) {
                setJobAlertsEnabled(newStatus);
            }
        } catch (error) {
            console.error('Error toggling job alerts:', error);
            alert('Failed to update job alerts preference. Please try again.');
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== "DELETE") {
            alert('Please type DELETE to confirm');
            return;
        }

        try {
            setIsDeleting(true);
            await axios.delete(
                `${process.env.REACT_APP_API_ENDPOINT}/recruiter/delete-student-user`,
                {
                    headers: {
                        Authorization: `Bearer ${getToken()}`,
                    },
                    data: { email: userEmail }
                }
            );
            
            // Clear all local storage and logout
            localStorage.clear();
            
            // Refresh and redirect to login
            window.location.href = "/login";
        } catch (error) {
            console.error('Error deleting account:', error);
            alert('Failed to delete account. Please try again or contact support.');
            setIsDeleting(false);
        }
    };

    return (
        <div className="mockInterviewerInput-page">
            {isLoading ? (
                <div className="w-full h-screen px-40 mobile-padding">
                    <Spinner />
                </div>
            ) : (
                <div className="main-container">
                    {!isMobile && (
                        <div className="side-menu-nora menu-add">
                            <ul>
                                <li onClick={() => navigate("/mockInterviewerInput")}>Dashboard</li>
                                {hasResume && (
                                    <li onClick={() => document.getElementById("resume").scrollIntoView({ behavior: 'smooth' })}>Resume</li>
                                )}
                                {/* <li onClick={() => document.getElementById("job-alerts").scrollIntoView({ behavior: 'smooth' })}>Job Alerts</li> */}
                                {isSubscribed && (
                                    <li onClick={() => document.getElementById("subscription").scrollIntoView({ behavior: 'smooth' })}>Subscription</li>
                                )}
                                <li className="referral-side-link" onClick={() => setOpenReferralModal(true)}>Referral</li>
                                <li onClick={() => document.getElementById("delete-account").scrollIntoView({ behavior: 'smooth' })} style={{ color: '#dc2626' }}>Delete Account</li>
                            </ul>
                        </div>
                    )}
                    
                    <div className="main-content">
                        <div className="section-title-input-2">Settings</div>

                        {/* Resume Section */}
                        {hasResume && (
                            <>
                                <div className="section-subtitle-input" id="resume">Your Resume</div>
                                <div className="settings-resume-section">
                                    <div className="resume-preview-card">
                                        <div className="resume-preview-header">
                                            <div className="resume-preview-info">
                                                <h3>{resumeFileName}</h3>
                                            </div>
                                            <button 
                                                className="inputButton-2"
                                                onClick={handleDeleteResume}
                                            >
                                                Delete Resume
                                            </button>
                                        </div>

                                        {/* Resume Viewer */}
                                        <div style={{ marginTop: "2rem" }}>
                                            <p style={{ marginBottom: "1rem" }}>
                                                <strong>Resume Preview</strong>
                                            </p>
                                            <div style={{
                                                borderTop: "1px solid #ccc", 
                                                width: "100%", 
                                                marginBottom: "1rem"
                                            }}/>
                                            {resumeUrl && (
                                                <iframe
                                                    key={`resume-viewer-${userEmail}`}
                                                    src={getDocumentViewerUrl(resumeUrl)}
                                                    width="100%"
                                                    height="600px"
                                                    style={{ 
                                                        border: "1px solid #e0e0e0",
                                                        borderRadius: "8px"
                                                    }}
                                                    frameBorder="0"
                                                    title="Resume Viewer"
                                                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                                                >
                                                    <p>
                                                        Your browser does not support iframes. 
                                                        <a 
                                                            href={resumeUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                        >
                                                            Click here to view the document.
                                                        </a>
                                                    </p>
                                                </iframe>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Job Alerts Section */}
                        {/* <div className="section-subtitle-input margin-top-sections" id="job-alerts">
                            Job Alerts
                        </div>
                        <div className="settings-resume-section">
                            <div className="job-alerts-card" style={{
                                padding: "24px",
                                backgroundColor: "#fff",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0"
                            }}>
                                <div style={{ 
                                    display: "flex", 
                                    justifyContent: "space-between", 
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: "16px"
                                }}>
                                    <div>
                                        <h3 style={{ margin: "0 0 8px 0", fontSize: "18px" }}>
                                            Weekly Job Matches
                                        </h3>
                                        <p style={{ 
                                            margin: 0, 
                                            color: "#666", 
                                            fontSize: "14px",
                                            maxWidth: "600px"
                                        }}>
                                            Get curated job openings that match your recent mock interviews sent to your inbox once a week
                                        </p>
                                    </div>
                                    <label style={{
                                        position: "relative",
                                        display: "inline-block",
                                        width: "60px",
                                        height: "34px",
                                        flexShrink: 0
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={jobAlertsEnabled}
                                            onChange={toggleJobAlerts}
                                            style={{ opacity: 0, width: 0, height: 0 }}
                                        />
                                        <span style={{
                                            position: "absolute",
                                            cursor: "pointer",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: jobAlertsEnabled ? "#000" : "#ccc",
                                            transition: "0.4s",
                                            borderRadius: "34px"
                                        }}>
                                            <span style={{
                                                position: "absolute",
                                                content: "",
                                                height: "26px",
                                                width: "26px",
                                                left: jobAlertsEnabled ? "30px" : "4px",
                                                bottom: "4px",
                                                backgroundColor: "white",
                                                transition: "0.4s",
                                                borderRadius: "50%"
                                            }}></span>
                                        </span>
                                    </label>
                                </div>
                                <div style={{
                                    marginTop: "16px",
                                    paddingTop: "16px",
                                    borderTop: "1px solid #e0e0e0",
                                    fontSize: "13px",
                                    color: "#999"
                                }}>
                                    {jobAlertsEnabled ? (
                                        <p style={{ margin: 0 }}>
                                            ✓ You'll receive job alerts based on your mock interview history
                                        </p>
                                    ) : (
                                        <p style={{ margin: 0 }}>
                                            Job alerts are turned off. You won't receive weekly job matches.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div> */}

                        {/* Subscription Section */}
                        {isSubscribed && (
                            <>
                                <div className="section-subtitle-input margin-top-sections" id="subscription">
                                    Manage your subscription
                                </div>
                                <div className="subscription-summary">
                                    <p className="subscription-caption">
                                        We've partnered with Stripe to manage your subscription. 
                                        Click the button to update your payment methods, billing details, or personal information. 
                                        Reach us at hannan@norahq.com for queries
                                    </p>

                                    <button 
                                        className={isMobile ? "inputButton-1 button-mobile" : "inputButton-1"} 
                                        onClick={() => window.open("https://billing.stripe.com/p/login/4gw5mvbyE9eB9nW4gg", "_blank")}
                                    >
                                        Manage Subscription
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Delete Account Section */}
                        <div className="section-subtitle-input margin-top-sections" id="delete-account" style={{ color: '#dc2626' }}>
                            Delete Account
                        </div>
                        <div className="settings-resume-section">
                            <div style={{
                                padding: "24px",
                                backgroundColor: "#fef2f2",
                                borderRadius: "8px",
                                border: "1px solid #fecaca"
                            }}>
                                <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#dc2626" }}>
                                    Permanently Delete Your Account
                                </h3>
                                <p style={{ 
                                    margin: "0 0 16px 0", 
                                    color: "#666", 
                                    fontSize: "14px",
                                    lineHeight: "1.5"
                                }}>
                                    This action cannot be undone. This will permanently delete your account, 
                                    interview history, saved resumes, and all associated data.
                                    {isSubscribed && (
                                        <span style={{ display: "block", marginTop: "8px", fontWeight: "500", color: "#dc2626" }}>
                                            Note: Please cancel your subscription in the Stripe portal before deleting your account.
                                        </span>
                                    )}
                                </p>

                                {!showDeleteConfirm ? (
                                    <button 
                                        onClick={() => setShowDeleteConfirm(true)}
                                        style={{
                                            padding: "10px 20px",
                                            backgroundColor: "#fff",
                                            color: "#dc2626",
                                            border: "1px solid #dc2626",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            transition: "all 0.2s"
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.backgroundColor = "#dc2626";
                                            e.target.style.color = "#fff";
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.backgroundColor = "#fff";
                                            e.target.style.color = "#dc2626";
                                        }}
                                    >
                                        Delete My Account
                                    </button>
                                ) : (
                                    <div style={{
                                        padding: "16px",
                                        backgroundColor: "#fff",
                                        borderRadius: "6px",
                                        border: "1px solid #fecaca"
                                    }}>
                                        <p style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: "500" }}>
                                            Type <strong>DELETE</strong> to confirm:
                                        </p>
                                        <input
                                            type="text"
                                            value={deleteConfirmText}
                                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                                            placeholder="Type DELETE"
                                            style={{
                                                width: "100%",
                                                padding: "10px 12px",
                                                border: "1px solid #e0e0e0",
                                                borderRadius: "6px",
                                                fontSize: "14px",
                                                marginBottom: "12px",
                                                boxSizing: "border-box"
                                            }}
                                        />
                                        <div style={{ display: "flex", gap: "12px" }}>
                                            <button
                                                onClick={() => {
                                                    setShowDeleteConfirm(false);
                                                    setDeleteConfirmText("");
                                                }}
                                                style={{
                                                    padding: "10px 20px",
                                                    backgroundColor: "#f3f4f6",
                                                    color: "#374151",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    fontSize: "14px"
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleDeleteAccount}
                                                disabled={deleteConfirmText !== "DELETE" || isDeleting}
                                                style={{
                                                    padding: "10px 20px",
                                                    backgroundColor: deleteConfirmText === "DELETE" ? "#dc2626" : "#f87171",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    cursor: deleteConfirmText === "DELETE" && !isDeleting ? "pointer" : "not-allowed",
                                                    fontSize: "14px",
                                                    fontWeight: "500",
                                                    opacity: deleteConfirmText === "DELETE" ? 1 : 0.6
                                                }}
                                            >
                                                {isDeleting ? "Deleting..." : "Permanently Delete Account"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ReferralRewardsModal
                open={openReferralModal}
                onClose={() => setOpenReferralModal(false)}
            />
        </div>
    );
};

export default Profile;
